"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { FlashcardSet, FlashcardSetRepetition, FlashcardSetStreamingChunk, NotePopulated, Share } from "@vita-learning/types";
import { type FlashcardQueryParams, FLASHCARDS_URL_SEGMENT, getFlashcardsKey, getFlashcardsUrl } from "~/shared/services/data/flashcard-sets/query";
import { createFlashcardSet, fetchFlashcardSets, updateFlashcardSet, createFlashcardsShareUrl, createManyFlashcardSets, deleteFlashcardSet, generateFlashcardSet, getFlashcardSet, updateFlashcardSetRepetition, shareFlashcardSet } from "~/shared/services/data/flashcard-sets/requests";

/**
 * Hook for fetching flashcard sets. If `id` is provided in the params, will fetch a single flashcard set. Otherwise, will fetch all flashcard sets (optionally filtered by `folderId`).
 *
 * @param {FlashcardQueryParams} params the parameters for fetching flashcard sets
 * @returns the flashcard set(s) along with mutation functions for creating, updating, deleting, sharing, and generating flashcard sets
 */
export const useFlashcardSets = t0 => {
  const $ = _c(41);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const params = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== params) {
    t2 = getFlashcardsKey(params);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== params) {
    t3 = () => {
      if (params.disabled) {
        return [];
      }
      if (params.id) {
        return getFlashcardSet(params.id);
      }
      const url = getFlashcardsUrl(params);
      return fetchFlashcardSets(url);
    };
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const queryResults = useSuspenseQuery(t4);
  let t5;
  if ($[9] !== queryClient) {
    t5 = () => queryClient.invalidateQueries({
      queryKey: [FLASHCARDS_URL_SEGMENT]
    });
    $[9] = queryClient;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const revalidateAll = t5;
  let t6;
  if ($[11] !== queryResults.data) {
    t6 = Array.isArray(queryResults.data) ? queryResults.data : [];
    $[11] = queryResults.data;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const data = t6;
  const entity = !Array.isArray(queryResults.data) ? queryResults.data : null;
  let t7;
  if ($[13] !== revalidateAll) {
    t7 = {
      mutationFn: createFlashcardSet,
      onSuccess: revalidateAll
    };
    $[13] = revalidateAll;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const create = useMutation(t7);
  let t8;
  if ($[15] !== revalidateAll) {
    t8 = {
      mutationFn: updateFlashcardSet,
      onSuccess: revalidateAll
    };
    $[15] = revalidateAll;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const update = useMutation(t8);
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      mutationFn: createFlashcardsShareUrl
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const createShareUrl = useMutation(t9);
  let t10;
  if ($[18] !== revalidateAll) {
    t10 = {
      mutationFn: createManyFlashcardSets,
      onSuccess: revalidateAll
    };
    $[18] = revalidateAll;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const createMany = useMutation(t10);
  let t11;
  if ($[20] !== revalidateAll) {
    t11 = {
      mutationFn: deleteFlashcardSet,
      onSuccess: revalidateAll
    };
    $[20] = revalidateAll;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const remove = useMutation(t11);
  let t12;
  if ($[22] !== revalidateAll) {
    t12 = {
      mutationFn: _temp,
      onSuccess: revalidateAll
    };
    $[22] = revalidateAll;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const updateRepetition = useMutation(t12);
  let t13;
  if ($[24] !== revalidateAll) {
    t13 = {
      mutationFn: _temp2,
      onSuccess: revalidateAll
    };
    $[24] = revalidateAll;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const share_0 = useMutation(t13);
  let t14;
  if ($[26] !== revalidateAll) {
    t14 = {
      mutationFn: _temp3,
      onSuccess: revalidateAll
    };
    $[26] = revalidateAll;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const generate = useMutation(t14);
  let t15;
  if ($[28] !== create || $[29] !== createMany || $[30] !== createShareUrl || $[31] !== data || $[32] !== entity || $[33] !== generate || $[34] !== queryResults || $[35] !== remove || $[36] !== revalidateAll || $[37] !== share_0 || $[38] !== update || $[39] !== updateRepetition) {
    t15 = {
      ...queryResults,
      data,
      entity,
      create,
      update,
      createShareUrl,
      createMany,
      remove,
      updateRepetition,
      share: share_0,
      generate,
      revalidateAll
    };
    $[28] = create;
    $[29] = createMany;
    $[30] = createShareUrl;
    $[31] = data;
    $[32] = entity;
    $[33] = generate;
    $[34] = queryResults;
    $[35] = remove;
    $[36] = revalidateAll;
    $[37] = share_0;
    $[38] = update;
    $[39] = updateRepetition;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
function _temp(t0) {
  const {
    id,
    repetition
  } = t0;
  return updateFlashcardSetRepetition(id, repetition);
}
function _temp2(t0) {
  const {
    id: id_0,
    share
  } = t0;
  return shareFlashcardSet(id_0, share);
}
function _temp3(t0) {
  const {
    flashcardSet,
    source,
    onStream
  } = t0;
  return generateFlashcardSet(flashcardSet, source, onStream);
}